//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import { Component } from "react";
import styled from "@emotion/styled";
import { RouteComponentProps } from "react-router-dom";
import { Button, Checkbox, Grid, Header, Icon, Label, Menu, Modal, Segment, Step, Message, SemanticICONS, Form } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import {
  AbortRunRequest as ApiAbortRunRequest,
  RunKind,
  Schedule as ApiSchedule,
  SetScheduleRequest as ApiSetScheduleRequest,
  StartRunRequest as ApiStartRunRequest,
  Status as ApiStatus,
} from "../../api/cd-robot/v1/cd_robot";
import { hasSupportPermission, Permission } from "../../util/PermissionCache";
import { IWithRefreshProps, withRefresh } from "../../util/WithRefresh";
import { ContentSegment, ErrorMessage, Processing, SecondaryMenu } from "../../ui/lib";
import { FormattedDateWithLabel, DateTimeTransformationTypes } from "../../util/dateAndTimeUtils/DateTime";
import { ScheduleTimeField } from "./ScheduleTimeField";
import { localTimezoneLabel } from "./cronSchedule";
import { hasRunAudit, normalizeRunKind } from "./auditDisplay";
import { RunAuditCard } from "./RunAuditCard";
import { ScheduleChangeCard } from "./ScheduleChangeCard";
import { CDRobotEvents } from "./CDRobotEvents";

const developmentBranch = "development";

const runPhases: { key: string; label: string; icon: SemanticICONS }[] = [
  { key: "CreateBranch", label: "QA branch", icon: "code branch" },
  { key: "UpdateFluxTag", label: "Flux switch", icon: "exchange" },
  { key: "WaitFluxReconcile", label: "Reconcile", icon: "sync" },
  { key: "WaitPodsStable", label: "Pods stable", icon: "heartbeat" },
  { key: "CheckAPICompatibleStart", label: "API compat", icon: "check circle" },
  { key: "RunIntegrationTests", label: "Tests", icon: "tasks" },
  { key: "StartDCUpdateLTS", label: "DC update", icon: "cloud upload" },
  { key: "ReportSuccess", label: "Done", icon: "flag checkered" },
];

const Hero = styled(Segment)`
  &&& {
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 45%, #0f3460 100%);
    color: #e8e8e8;
    border: none;
    box-shadow: 0 8px 32px rgba(15, 52, 96, 0.35);
  }
`;

const FluxChip = styled(Label)`
  &&& {
    font-family: "Consolas", "Monaco", monospace;
    font-size: 0.95em;
    margin-top: 0.5rem;
  }
`;

const StateBadge = styled(Label)<{ state?: string }>`
  &&& {
    font-size: 1.1em;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }
`;

const phaseIndex = (runningState?: string): number => {
  if (!runningState) {
    return -1;
  }
  const direct = runPhases.findIndex((p) => p.key === runningState);
  if (direct >= 0) {
    return direct;
  }
  // Map related states onto the nearest visible phase
  const fallback: Record<string, number> = {
    Infer: 0,
    SetDataClustersUnassignable: 4,
    ExcludeNonLTSDataClustersFromUpdate: 6,
    UpdateLTSDataClusters: 6,
    WaitDCUpdateReadyLTS: 6,
    CheckAPICompatibleLTSUpdated: 6,
    SetDataClustersUnassignableButLTS: 6,
    RunLTSIntegrationTests: 6,
  };
  return fallback[runningState] ?? 4;
};

// The API returns enum values as strings in JSON, while the generated TS enum is numeric.
const runKindBadge = (runKind?: RunKind | string, state?: string): { label: string; icon: SemanticICONS; color: "blue" | "purple" } | undefined => {
  if (state !== "Running" && state !== "Stopping") {
    return undefined;
  }
  switch (normalizeRunKind(runKind)) {
    case RunKind.RUN_KIND_SCHEDULED:
      return { label: "Nightly", icon: "moon", color: "blue" };
    case RunKind.RUN_KIND_MANUAL:
      return { label: "Manual", icon: "hand pointer", color: "purple" };
    default:
      return undefined;
  }
};

const stateColor = (state?: string): "grey" | "green" | "orange" | "blue" => {
  switch (state) {
    case "Running":
      return "green";
    case "Stopping":
      return "orange";
    case "Waiting":
      return "blue";
    default:
      return "grey";
  }
};

interface ICDRobotViewArgs {
  status?: ApiStatus;
  scheduleDraft: ApiSchedule;
  scheduleDirty: boolean;
  canEditSchedule: boolean;
  onScheduleChange: (field: keyof ApiSchedule, value: string | boolean) => void;
  onSaveSchedule: () => void;
  savingSchedule: boolean;
}

const CDRobotView = ({ status, scheduleDraft, scheduleDirty, canEditSchedule, onScheduleChange, onSaveSchedule, savingSchedule }: ICDRobotViewArgs) => {
  const activePhase = phaseIndex(status?.running_state);
  const isActive = status?.state === "Running" || status?.state === "Stopping";

  return (
    <>
      <Hero>
        <Grid stackable verticalAlign="middle">
          <Grid.Column width={10}>
            <Header inverted style={{ marginBottom: "0.25rem" }}>
              <Icon name="rocket" />
              CD Robot
            </Header>
            <div style={{ opacity: 0.85 }}>Controlplane CD validation — branch, Flux, tests, teardown.</div>
            {status?.flux_branch && (
              <FluxChip color="teal" image={<Icon name="git" />}>
                Flux branch: {status.flux_branch}
              </FluxChip>
            )}
          </Grid.Column>
          <Grid.Column width={6} textAlign="right">
            <StateBadge state={status?.state} color={stateColor(status?.state)} size="large">
              {status?.state === "Running" && <Icon name="circle notched" loading />}
              {status?.state || "Unknown"}
            </StateBadge>
            {(() => {
              const badge = runKindBadge(status?.run_kind, status?.state);
              return badge ? (
                <Label color={badge.color} style={{ marginLeft: "0.5rem", verticalAlign: "middle" }} size="large">
                  <Icon name={badge.icon} />
                  {badge.label}
                </Label>
              ) : null;
            })()}
            {status?.running_state && status.state === "Running" && (
              <div style={{ marginTop: "0.75rem", opacity: 0.9, fontSize: "0.95em" }}>{status.running_state}</div>
            )}
            {status?.stopping_state && status.state === "Stopping" && (
              <div style={{ marginTop: "0.75rem", opacity: 0.9, fontSize: "0.95em" }}>{status.stopping_state}</div>
            )}
            {status?.running_test_state && <div style={{ marginTop: "0.35rem", opacity: 0.75, fontSize: "0.9em" }}>Tests: {status.running_test_state}</div>}
          </Grid.Column>
        </Grid>
      </Hero>

      {status?.last_error && (
        <Message negative icon="warning sign">
          <Message.Content>
            <Message.Header>Last error</Message.Header>
            {status.last_error_time && (
              <div style={{ marginBottom: "0.5rem", opacity: 0.85 }}>
                <FormattedDateWithLabel label="Occurred" dateTime={status.last_error_time} transformTo={DateTimeTransformationTypes.LOCAL} />
              </div>
            )}
            {status.last_error}
          </Message.Content>
        </Message>
      )}

      {isActive && (
        <Segment>
          <Header sub>Runway</Header>
          <Step.Group fluid size="mini">
            {runPhases.map((phase, idx) => {
              const completed = activePhase > idx;
              const active = activePhase === idx;
              return (
                <Step key={phase.key} completed={completed} active={active} disabled={!completed && !active}>
                  <Icon name={phase.icon} />
                  <Step.Content>
                    <Step.Title>{phase.label}</Step.Title>
                  </Step.Content>
                </Step>
              );
            })}
          </Step.Group>
        </Segment>
      )}

      <Segment>
        <Header sub>Schedule</Header>
        <Message info size="small">
          cd-robot runs on a <strong>daily</strong> schedule stored as UTC cron in the cluster ConfigMap. Pick times in your timezone ({localTimezoneLabel()}) —
          they are converted automatically.
        </Message>
        <Form>
          <ScheduleTimeField
            label="Start run"
            help="When the nightly CD cycle begins (branch, Flux switch, tests)."
            cron={scheduleDraft.start_schedule || ""}
            disabled={!canEditSchedule}
            onCronChange={(value) => onScheduleChange("start_schedule", value)}
          />
          <ScheduleTimeField
            label="Begin stop"
            help="When teardown starts and Flux moves back toward development."
            cron={scheduleDraft.stop_schedule || ""}
            disabled={!canEditSchedule}
            onCronChange={(value) => onScheduleChange("stop_schedule", value)}
          />
          <ScheduleTimeField
            label="Fully stopped"
            help="Deadline by which the run must be completely torn down."
            cron={scheduleDraft.stopped_schedule || ""}
            disabled={!canEditSchedule}
            onCronChange={(value) => onScheduleChange("stopped_schedule", value)}
          />
          <Form.Field>
            <label>Pre-flight lead time</label>
            <div style={{ color: "#767676", fontSize: "0.9em", margin: "0.25rem 0 0.5rem" }}>
              How long before Start run to Slack merge + API-compat pre-flight (Go duration, e.g. <code>8h</code>, <code>30m</code>). Raise this to force the
              window open for staging checks.
            </div>
            <Form.Input
              placeholder="8h"
              value={scheduleDraft.preflight_lead_time || ""}
              disabled={!canEditSchedule}
              onChange={(_, d) => onScheduleChange("preflight_lead_time", (d.value as string) || "")}
            />
          </Form.Field>
        </Form>
        <div style={{ marginTop: "1rem" }}>
          <Checkbox
            toggle
            label="Run on weekends (Fri & Sat)"
            checked={!!scheduleDraft.run_in_weekends}
            disabled={!canEditSchedule}
            onChange={(_, d) => onScheduleChange("run_in_weekends", !!d.checked)}
          />
        </div>
        {canEditSchedule && scheduleDirty && (
          <Button primary icon="save" content="Save schedule" loading={savingSchedule} onClick={onSaveSchedule} style={{ marginTop: "1rem" }} />
        )}
      </Segment>

      {hasRunAudit(status?.current_run) && <RunAuditCard title="Current run" run={status?.current_run} inProgress />}
      {hasRunAudit(status?.last_run) && <RunAuditCard title="Last completed run" run={status?.last_run} />}
      <ScheduleChangeCard change={status?.last_schedule_change} />
      <CDRobotEvents />
    </>
  );
};

interface ICDRobotProps extends IWithRefreshProps, RouteComponentProps {}

interface ICDRobotState {
  errorMessage?: string;
  errorMessageTime?: Date;
  status?: ApiStatus;
  scheduleDraft: ApiSchedule;
  scheduleDirty: boolean;
  createNewQABranch: boolean;
  processingStart: boolean;
  processingAbort: boolean;
  savingSchedule: boolean;
  confirmStartOpen: boolean;
  confirmAbortOpen: boolean;
}

class CDRobot extends Component<ICDRobotProps, ICDRobotState> {
  pollTimer?: number;

  state: ICDRobotState = {
    scheduleDraft: {},
    scheduleDirty: false,
    createNewQABranch: false,
    processingStart: false,
    processingAbort: false,
    savingSchedule: false,
    confirmStartOpen: false,
    confirmAbortOpen: false,
  };

  hasPermission = (permission: Permission) => hasSupportPermission(permission, this.props.hasPermissionByUrl);

  setPageError = (message: string) => {
    this.setState({ errorMessage: message, errorMessageTime: new Date() });
  };

  refreshStatus = async () => {
    try {
      const status = await apiClients.idashboardClient.GetCDRobotStatus();
      this.setState((prev) => ({
        status,
        scheduleDraft: prev.scheduleDirty ? prev.scheduleDraft : status.schedule || {},
        errorMessage: undefined,
        errorMessageTime: undefined,
      }));
    } catch (e) {
      this.setPageError(`Failed to get CD Robot status: ${e}`);
    }
  };

  refreshSchedule = async () => {
    if (!this.hasPermission("internal-dashboard.cd-robot.schedule.get")) {
      return;
    }
    try {
      const schedule = await apiClients.idashboardClient.GetCDRobotSchedule();
      if (!this.state.scheduleDirty) {
        this.setState({ scheduleDraft: schedule });
      }
    } catch (e) {
      this.setPageError(`Failed to get schedule: ${e}`);
    }
  };

  handleDismissError = () => this.setState({ errorMessage: undefined, errorMessageTime: undefined });

  onScheduleChange = (field: keyof ApiSchedule, value: string | boolean) => {
    this.setState((prev) => ({
      scheduleDraft: { ...prev.scheduleDraft, [field]: value },
      scheduleDirty: true,
    }));
  };

  onSaveSchedule = async () => {
    this.setState({ savingSchedule: true });
    try {
      const req: ApiSetScheduleRequest = { schedule: this.state.scheduleDraft };
      const schedule = await apiClients.idashboardClient.SetCDRobotSchedule(req);
      this.setState({ scheduleDraft: schedule, scheduleDirty: false });
    } catch (e) {
      this.setPageError(`Failed to save schedule: ${e}`);
    }
    this.setState({ savingSchedule: false });
  };

  onStartClick = () => {
    const { createNewQABranch, status } = this.state;
    if (createNewQABranch && status?.flux_branch && status.flux_branch !== developmentBranch) {
      this.setState({ confirmStartOpen: true });
      return;
    }
    this.onStart();
  };

  onStart = async () => {
    this.setState({ processingStart: true, confirmStartOpen: false });
    try {
      const req: ApiStartRunRequest = {
        create_new_qa_branch: this.state.createNewQABranch,
      };
      const status = await apiClients.idashboardClient.StartCDRobotRun(req);
      this.setState({ status, createNewQABranch: false });
    } catch (e) {
      this.setPageError(`Failed to start CD run: ${e}`);
    }
    this.setState({ processingStart: false });
  };

  onAbort = async () => {
    this.setState({ processingAbort: true, confirmAbortOpen: false });
    try {
      const req: ApiAbortRunRequest = {};
      const status = await apiClients.idashboardClient.AbortCDRobotRun(req);
      this.setState({ status });
    } catch (e) {
      this.setPageError(`Failed to abort CD run: ${e}`);
    }
    this.setState({ processingAbort: false });
  };

  schedulePoll = () => {
    const interval = this.state.status?.state === "Running" || this.state.status?.state === "Stopping" ? 3000 : 15000;
    this.pollTimer = window.setTimeout(async () => {
      await this.refreshStatus();
      this.schedulePoll();
    }, interval);
  };

  componentDidMount() {
    this.refreshStatus();
    this.refreshSchedule();
    this.schedulePoll();
  }

  componentWillUnmount() {
    if (this.pollTimer) {
      window.clearTimeout(this.pollTimer);
    }
  }

  render() {
    const { status, createNewQABranch, confirmStartOpen, confirmAbortOpen } = this.state;
    const hasStart = this.hasPermission("internal-dashboard.cd-robot.run.start");
    const hasAbort = this.hasPermission("internal-dashboard.cd-robot.run.abort");
    const canEditSchedule = this.hasPermission("internal-dashboard.cd-robot.schedule.set");
    const isStopping = status?.state === "Stopping";
    const canStart = !!status?.can_start && !isStopping;
    const canAbort = !!status?.can_abort && !isStopping;

    return (
      <ContentSegment>
        <ErrorMessage
          active={!!this.state.errorMessage}
          onDismiss={this.handleDismissError}
          message={this.state.errorMessage}
          occurredAt={this.state.errorMessageTime}
        />
        <Processing active={this.state.processingStart} message="Starting CD run…" />
        <Processing active={this.state.processingAbort} message="Aborting CD run…" />

        <SecondaryMenu>
          <Menu.Item header>CD Robot</Menu.Item>
          <Menu.Menu position="right">
            {(hasStart || hasAbort) && (
              <>
                <Menu.Item>
                  <Checkbox
                    toggle
                    label="New QA branch"
                    checked={createNewQABranch}
                    disabled={!canStart || this.state.processingStart}
                    onChange={(_, d) => this.setState({ createNewQABranch: !!d.checked })}
                  />
                </Menu.Item>
                {hasStart && (
                  <Menu.Item>
                    <Button primary icon="play" content="Start run" disabled={!canStart || this.state.processingStart} onClick={this.onStartClick} />
                  </Menu.Item>
                )}
                {hasAbort && (
                  <Menu.Item>
                    <Button
                      negative
                      icon="stop"
                      content="Abort"
                      disabled={!canAbort || this.state.processingAbort}
                      onClick={() => this.setState({ confirmAbortOpen: true })}
                    />
                  </Menu.Item>
                )}
              </>
            )}
          </Menu.Menu>
        </SecondaryMenu>

        <CDRobotView
          status={status}
          scheduleDraft={this.state.scheduleDraft}
          scheduleDirty={this.state.scheduleDirty}
          canEditSchedule={canEditSchedule && status?.state !== "Running" && status?.state !== "Stopping"}
          onScheduleChange={this.onScheduleChange}
          onSaveSchedule={this.onSaveSchedule}
          savingSchedule={this.state.savingSchedule}
        />

        <Modal open={confirmStartOpen} size="small" onClose={() => this.setState({ confirmStartOpen: false })}>
          <Modal.Header>Create new QA branch?</Modal.Header>
          <Modal.Content>
            <p>
              You are not on the <strong>{developmentBranch}</strong> branch. Are you sure you want to create a new QA branch?
            </p>
            <p>
              Current branch: <FluxChip>{status?.flux_branch}</FluxChip>
            </p>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={() => this.setState({ confirmStartOpen: false })}>Cancel</Button>
            <Button negative onClick={this.onStart}>
              Start anyway
            </Button>
          </Modal.Actions>
        </Modal>

        <Modal open={confirmAbortOpen} size="small" onClose={() => this.setState({ confirmAbortOpen: false })}>
          <Modal.Header>Abort CD run?</Modal.Header>
          <Modal.Content>This will stop the current run and begin teardown.</Modal.Content>
          <Modal.Actions>
            <Button onClick={() => this.setState({ confirmAbortOpen: false })}>Cancel</Button>
            <Button negative onClick={this.onAbort}>
              Abort run
            </Button>
          </Modal.Actions>
        </Modal>
      </ContentSegment>
    );
  }
}

export default withRefresh()(CDRobot);
